/**
 * Button Utilities
 * 
 * Shared utilities for button components including:
 * - Base class builders
 * - Focus ring utilities
 * - Loading spinner component
 * - Icon positioning utilities
 */

/**
 * Builds base button classes that are common across all button variants
 * @param {Object} options - Configuration options
 * @param {string} options.size - Button size ('sm', 'md', 'lg')
 * @param {boolean} options.disabled - Whether button is disabled
 * @param {boolean} options.fullWidth - Whether button should take full width
 * @param {boolean} options.loading - Whether button is in loading state
 * @returns {string} Space-separated CSS classes
 */
export const getBaseButtonClasses = ({ size = 'md', disabled = false, fullWidth = false, loading = false }) => {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:pointer-events-none',
  ];

  // Size-specific classes
  const sizeClasses = {
    sm: ['px-3', 'py-1.5', 'text-sm', 'rounded-md'],
    md: ['px-4', 'py-2', 'text-base', 'rounded-md'],
    lg: ['px-6', 'py-3', 'text-lg', 'rounded-md'],
  };

  // Add size classes
  baseClasses.push(...(sizeClasses[size] || sizeClasses.md));

  // Full width
  if (fullWidth) {
    baseClasses.push('w-full');
  }

  // Loading cursor
  if (loading) {
    baseClasses.push('cursor-wait');
  }

  return baseClasses.join(' ');
};

/**
 * Gets focus ring color classes based on button variant
 * @param {string} variant - Button variant ('primary', 'secondary', 'ghost', 'danger', 'success')
 * @returns {string} Focus ring classes
 */
export const getFocusRingClasses = (variant = 'primary') => {
  const focusRings = {
    primary: 'focus:ring-blue-500',
    gradient: 'focus:ring-blue-500',
    secondary: 'focus:ring-gray-500',
    ghost: 'focus:ring-blue-500',
    danger: 'focus:ring-red-500',
    success: 'focus:ring-green-500',
  };

  return focusRings[variant] || focusRings.primary;
};

/**
 * Loading Spinner Component
 * @param {Object} props - Component props
 * @param {string} props.size - Spinner size ('sm', 'md', 'lg')
 * @param {string} props.color - Spinner color (CSS color value)
 * @returns {JSX.Element} Spinner component
 */
export const LoadingSpinner = ({ size = 'md', color = 'currentColor' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke={color}
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill={color}
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

/**
 * Gets icon positioning classes
 * @param {string} iconPosition - Icon position ('left' or 'right')
 * @param {boolean} hasChildren - Whether button has text children
 * @returns {string} Icon spacing classes
 */
export const getIconSpacingClasses = (iconPosition, hasChildren) => {
  if (!hasChildren) {
    return ''; // Icon-only button, no spacing needed
  }

  if (iconPosition === 'left') {
    return 'mr-2';
  }
  
  if (iconPosition === 'right') {
    return 'ml-2';
  }

  return '';
};

/**
 * Wraps children with loading spinner if loading
 * @param {boolean} loading - Whether button is loading
 * @param {React.ReactNode} children - Button children
 * @param {string} size - Button size
 * @returns {React.ReactNode} Children with optional spinner
 */
export const wrapWithLoadingSpinner = (loading, children, size = 'md') => {
  if (!loading) {
    return children;
  }

  return (
    <>
      <LoadingSpinner size={size} />
      <span className="ml-2">{children}</span>
    </>
  );
};
